// ProtectedRoute — gates child routes on auth status.
//
// Renders:
//   - loading state while auth is resolving
//   - <Navigate to="/login"> if unauthenticated
//   - <Navigate to="/not-provisioned"> if authenticated but no portal_users row
//   - <Navigate> to the typed-error recovery route if status is the
//     discriminated `{ kind: 'error', reason }` shape (ADR-018)
//   - <Outlet> if fully authenticated

import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth, isAuthError, type AuthErrorReason } from '../../lib/auth';

function reasonToRoute(reason: AuthErrorReason): string {
  switch (reason) {
    case 'link_expired':
      return '/auth/expired';
    case 'link_invalid':
      return '/auth/invalid-link';
    case 'link_consumed':
      return '/auth/already-used';
    case 'rls_failure':
    case 'network_timeout':
    case 'pkce_exchange_failed':
    case 'unknown':
    default:
      return '/auth/error';
  }
}

export function ProtectedRoute(): React.ReactNode {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-cream text-text-muted"
      >
        <span className="text-[14px]">Loading…</span>
      </div>
    );
  }

  if (isAuthError(status)) {
    return <Navigate to={reasonToRoute(status.reason)} replace />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (status === 'unprovisioned') {
    return <Navigate to="/not-provisioned" replace />;
  }

  return <Outlet />;
}
